import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Upload, Sparkles, FileCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/how-it-works")({
  head: () => ({
    meta: [
      { title: "How legaldecoder Works — Decode Contracts in 3 Steps" },
      {
        name: "description",
        content:
          "Upload, analyze, and understand any contract in under 30 seconds. See how legaldecoder protects you from predatory clauses.",
      },
      { property: "og:title", content: "How legaldecoder Works" },
      {
        property: "og:description",
        content: "Three simple steps from confusing contract to clear understanding.",
      },
      { property: "og:url", content: "https://legaldecoder.io/how-it-works" },
    ],
    links: [{ rel: "canonical", href: "https://legaldecoder.io/how-it-works" }],
  }),
  component: HowItWorks,
});

const steps = [
  {
    n: "01",
    icon: Upload,
    title: "Upload or Paste",
    desc: "Drop a PDF, snap a photo, or paste contract text. Lease, NDA, freelance agreement — anything legal.",
  },
  {
    n: "02",
    icon: Sparkles,
    title: "AI Decodes Instantly",
    desc: "Our specialized models identify key terms, obligations, and predatory clauses in under 10 seconds.",
  },
  {
    n: "03",
    icon: FileCheck,
    title: "Understand & Decide",
    desc: "Get a plain-English verdict, key dates and fees, and red flags highlighted. Sign confidently — or walk away.",
  },
];

function HowItWorks() {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="relative overflow-hidden border-b border-border">
          <div className="absolute inset-0 bg-gradient-glow" aria-hidden />
          <div className="relative mx-auto max-w-4xl px-4 py-20 text-center sm:px-6 sm:py-28 lg:px-8">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
              From legalese to clarity in{" "}
              <span className="bg-gradient-to-r from-primary-glow to-accent bg-clip-text text-transparent">
                30 seconds.
              </span>
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-muted-foreground">
              No law degree required. Three steps stand between you and confidence.
            </p>
          </div>
        </section>

        <section className="py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-8 md:grid-cols-3">
              {steps.map((step) => (
                <div
                  key={step.n}
                  className="relative rounded-2xl border border-border bg-card p-8 shadow-card"
                >
                  <div className="absolute -top-3 left-6 rounded-full bg-foreground px-3 py-1 text-xs font-bold text-background">
                    STEP {step.n}
                  </div>
                  <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-glow shadow-elegant">
                    <step.icon className="h-7 w-7 text-primary-foreground" strokeWidth={2.25} />
                  </div>
                  <h3 className="text-xl font-semibold">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-16 rounded-2xl border border-border bg-gradient-hero p-10 text-center shadow-elegant">
              <h2 className="text-2xl font-bold text-primary-foreground sm:text-3xl">
                Ready to decode your first contract?
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-primary-foreground/80">
                Free to try. No signup required. Zero data retention.
              </p>
              <Button variant="hero" size="xl" className="mt-6 gap-2" asChild>
                <Link to="/">
                  Analyze a Contract
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </div>
  );
}
